import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import {
  getGetApiMessagesTemplatesIdQueryKey,
  useGetApiMessagesTemplatesId,
  usePostApiMessagesTemplates,
  usePutApiMessagesTemplatesId,
} from '@/api/generated/messages';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

const formSchema = z.object({
  code: z.string().min(1, 'Code is required (e.g. PAYMENT_REMINDER)'),
  title: z.string().min(1, 'Title is required'),
  body: z.string().min(1, 'Content is required'),
  isActive: z.boolean(),
});

export default function EmailTemplateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== 'new';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      title: '',
      body: '',
      isActive: true,
    },
  });

  // Fetch template data using generated hook
  const { data: result, isLoading: fetching } = useGetApiMessagesTemplatesId(id || '', {
    query: {
      enabled: !!isEdit && !!id,
      queryKey: getGetApiMessagesTemplatesIdQueryKey(id || ''),
    },
  });
  const templateData = result?.data;

  useEffect(() => {
    if (templateData) {
      form.reset({
        code: templateData.code,
        title: templateData.title,
        body: templateData.body,
        isActive: templateData.isActive ?? true,
      });
    }
  }, [templateData, form]);

  const updateMutation = usePutApiMessagesTemplatesId();
  const createMutation = usePostApiMessagesTemplates();

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({
          id,
          data: values,
        });
      } else {
        await createMutation.mutateAsync({
          data: values,
        });
      }

      toast({
        title: 'Success',
        description: 'Template saved successfully',
      });
      navigate('/tenant/content/emails');
    } catch (error) {
      console.error(error); // Log the error
      toast({
        title: 'Error',
        description: 'Failed to save template',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold tracking-tight">{isEdit ? 'Edit Template' : 'New Template'}</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template Code (Unique)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. PAYMENT_REMINDER" {...field} disabled={!!isEdit} />
                    </FormControl>
                    <FormDescription>System identifier used in code. Cannot be changed once created.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject / Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Payment Reminder" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Body Content</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Hello {{name}}, ..." className="min-h-[200px] font-mono" {...field} />
                    </FormControl>
                    <FormDescription>Supports basic variables (e.g. {'{{name}}'}).</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Status</FormLabel>
                      <FormDescription>Disable to prevent this template from being used.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="mr-2 h-4 w-4" />
                  Save Template
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
